import { useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { acceptInvite, previewInvite } from '@/api/members'
import { ROUTES } from '@/routes/routes'
import { currentHousehold } from '@/lib/currentHousehold'

function parseError(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status
  const detail = (err as { response?: { data?: { detail?: string; message?: string } } })?.response
    ?.data
  if (status === 410)
    return 'This invite is no longer valid. Ask the household owner to send a new one.'
  if (status === 403)
    return detail?.detail || detail?.message || 'You do not have permission to accept this invite.'
  if (status === 409) {
    if (detail?.detail === 'HOUSEHOLD_LIMIT_REACHED') {
      return "You're already in 3 households — the most you can be in at once. Leave one, then try again."
    }
    return detail?.detail || detail?.message || 'Unable to accept this invite.'
  }
  return 'Something went wrong. Please try again.'
}

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isLoaded, isSignedIn } = useAuth()

  const acceptMutation = useMutation({
    mutationFn: (t: string) => acceptInvite(t),
    onSuccess: (data) => {
      currentHousehold.set(data.householdId)
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      queryClient.invalidateQueries({ queryKey: ['household'] })
      queryClient.invalidateQueries({ queryKey: ['myHouseholds'] })
      setTimeout(() => navigate(ROUTES.APP.DASHBOARD), 1500)
    },
  })

  // Only fetched pre-sign-in — once signed in we go straight to accepting, no need to preview
  // what we're about to do.
  const previewQuery = useQuery({
    queryKey: ['invitePreview', token],
    queryFn: () => previewInvite(token!),
    enabled: !!token && isLoaded && !isSignedIn,
    retry: false,
  })

  useEffect(() => {
    if (isSignedIn && token && acceptMutation.isIdle) {
      acceptMutation.mutate(token)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, token])

  const noToken = !token

  return (
    <div className="min-h-screen bg-[var(--fl-surface-subtle)] flex items-center justify-center p-6">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-sm border border-[var(--fl-border)] p-8 max-w-md w-full text-center">
        <div className="mb-4">
          <span className="font-['Sora',system-ui,sans-serif] text-xl text-[var(--fl-text-3)]">
            Family
          </span>
          <span className="font-['Sora',system-ui,sans-serif] text-xl font-semibold text-[var(--fl-heading)]">
            Ledger
          </span>
        </div>

        {noToken && (
          <>
            <ErrorIcon />
            <h2 className="text-lg font-semibold text-[var(--fl-heading)] mb-2">
              Unable to accept invite
            </h2>
            <p className="text-sm text-[var(--fl-text-2)]">Invalid invite link.</p>
          </>
        )}

        {!noToken && !isLoaded && <Spinner label="Loading…" />}

        {!noToken && isLoaded && !isSignedIn && (
          <>
            {previewQuery.isPending && <Spinner label="Loading invite…" />}

            {previewQuery.isError && (
              <>
                <ErrorIcon />
                <h2 className="text-lg font-semibold text-[var(--fl-heading)] mb-2">
                  Unable to accept invite
                </h2>
                <p className="text-sm text-[var(--fl-text-2)]">{parseError(previewQuery.error)}</p>
              </>
            )}

            {previewQuery.isSuccess && (
              <>
                <h2 className="text-lg font-semibold text-[var(--fl-heading)] mb-2">
                  You've been invited to join
                </h2>
                <p className="text-xl font-semibold text-[var(--fl-primary)] mb-1">
                  {previewQuery.data.householdName}
                </p>
                <p className="text-sm text-[var(--fl-text-2)] mb-6">
                  Invited by {previewQuery.data.inviterName} — sign in or create an account with{' '}
                  <span className="font-medium text-[var(--fl-text-1)]">
                    {previewQuery.data.invitedEmail}
                  </span>{' '}
                  to accept.
                </p>
                <div className="flex flex-col gap-2">
                  <Link
                    to={`${ROUTES.AUTH.SIGN_UP}?redirectTo=${encodeURIComponent(`${ROUTES.APP.ACCEPT_INVITE}?token=${token}`)}`}
                    className="w-full rounded-lg bg-[var(--fl-primary)] px-4 py-2.5 text-sm font-medium text-[var(--fl-primary-fg)] hover:bg-[var(--fl-primary-hover)]"
                  >
                    Sign up to accept
                  </Link>
                  <Link
                    to={`${ROUTES.AUTH.SIGN_IN}?redirectTo=${encodeURIComponent(`${ROUTES.APP.ACCEPT_INVITE}?token=${token}`)}`}
                    className="w-full rounded-lg border border-[var(--fl-border)] px-4 py-2.5 text-sm font-medium text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]"
                  >
                    Sign in to accept
                  </Link>
                </div>
              </>
            )}
          </>
        )}

        {!noToken && isLoaded && isSignedIn && (
          <>
            {(acceptMutation.isPending || acceptMutation.isIdle) && (
              <Spinner label="Accepting your invitation…" />
            )}

            {acceptMutation.isSuccess && (
              <>
                <div className="w-12 h-12 rounded-full bg-[var(--fl-primary-faint)] flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-[var(--fl-heading)] mb-2">
                  You've joined the household!
                </h2>
                <p className="text-sm text-[var(--fl-text-3)]">Redirecting to your dashboard…</p>
              </>
            )}

            {acceptMutation.isError && (
              <>
                <ErrorIcon />
                <h2 className="text-lg font-semibold text-[var(--fl-heading)] mb-2">
                  Unable to accept invite
                </h2>
                <p className="text-sm text-[var(--fl-text-2)]">
                  {parseError(acceptMutation.error)}
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function Spinner({ label }: { label: string }) {
  return (
    <>
      <div className="w-8 h-8 border-2 border-[var(--fl-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-sm text-[var(--fl-text-2)]">{label}</p>
    </>
  )
}

function ErrorIcon() {
  return (
    <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
      <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </div>
  )
}
